import axios from 'axios';
import safetyEvents from './safetyEvents';

const API = process.env.REACT_APP_API_URL || '/api';
const TEMPLATE_CACHE_TTL = 5 * 60 * 1000;
let templatesCache = null;
let templatesCacheAt = 0;
let templatesRequest = null;

const getHeaders = (token) => ({
  headers: { Authorization: `Bearer ${token}` }
});

// ── Global Safety Interceptor ─────────────────────────────────────────────
// Intercepts 503 SERVICE_TIMEOUT and 403 ACCOUNT_SUSPENDED responses from
// any generation API call and triggers the SafetyBanner to refresh.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status  = error.response?.status;
    const errCode = error.response?.data?.error;

    if (status === 503 && errCode === 'SERVICE_TIMEOUT') {
      safetyEvents.emit('refresh', { reason: 'timeout' });
    } else if (status === 403 && errCode === 'ACCOUNT_SUSPENDED') {
      safetyEvents.emit('refresh', { reason: 'suspended' });
    }

    return Promise.reject(error);
  }
);

// ── User ──────────────────────────────────────────────────────────────────
export async function fetchCredits(token) {
  const r = await axios.get(`${API}/user/credits`, getHeaders(token));
  return r.data;
}

export async function fetchProfile(token) {
  const r = await axios.get(`${API}/user/profile`, getHeaders(token));
  return r.data;
}

export async function fetchGenerations(token) {
  const r = await axios.get(`${API}/user/generations`, getHeaders(token));
  return r.data.generations || [];
}

export async function fetchGeneration(token, id) {
  const r = await axios.get(`${API}/generation/${id}`, getHeaders(token));
  return r.data.generation;
}

export async function deleteGeneration(token, id) {
  await axios.delete(`${API}/generation/${id}`, getHeaders(token));
}

export async function batchDeleteGenerations(token, ids) {
  const r = await axios.post(`${API}/generations/batch-delete`, { ids }, getHeaders(token));
  return r.data;
}

// ── Models ────────────────────────────────────────────────────────────────
export async function fetchModels(token) {
  const r = await axios.get(`${API}/models`, getHeaders(token));
  return r.data;
}

export async function calcCost(token, params) {
  const r = await axios.post(`${API}/calc-cost`, params, getHeaders(token));
  return r.data.cost;
}

// ── Image Generation ──────────────────────────────────────────────────────
export async function generateImage(token, { prompt, model, aspectRatio, negativePrompt, references, imageResolution, quality, nsfwChecker }) {
  const fd = new FormData();
  fd.append('prompt', prompt);
  fd.append('model', model);
  fd.append('aspectRatio', aspectRatio || '1:1');
  if (negativePrompt) fd.append('negativePrompt', negativePrompt);
  if (imageResolution) fd.append('imageResolution', imageResolution);
  if (quality) fd.append('quality', quality);
  if (nsfwChecker !== undefined) fd.append('nsfwChecker', String(nsfwChecker));
  if (references) {
    const refUrls = [];
    references.forEach(f => {
      if (typeof f === 'string') refUrls.push(f);
      else fd.append('references', f);
    });
    if (refUrls.length > 0) fd.append('referenceUrls', JSON.stringify(refUrls));
  }

  // Returns { jobId, creditsRemaining, status: 'queued' } — use pollJob() to wait for result
  const r = await axios.post(`${API}/generate/image`, fd, {
    headers: getHeaders(token).headers,
    timeout: 30000, // just submission — fast
  });
  return r.data;
}

// ── Job Queue Polling ─────────────────────────────────────────────────────
// Poll a job until completed/failed. Calls onProgress({ status, elapsed }) on each tick.
export async function pollJob(token, jobId, onProgress, timeoutMs = 300000) {
  const startTime = Date.now();
  let interval = 2000; // start polling at 2s
  const MAX_INTERVAL = 6000;

  return new Promise((resolve, reject) => {
    let timer;

    const check = async () => {
      try {
        const r = await axios.get(`${API}/jobs/${jobId}`, getHeaders(token));
        const job = r.data;

        if (onProgress) onProgress({ status: job.status, elapsed: Date.now() - startTime });

        if (job.status === 'completed') {
          clearTimeout(timer);
          resolve(job);
          return;
        }
        if (job.status === 'failed') {
          clearTimeout(timer);
          reject(new Error(job.errorMsg || 'Generation failed'));
          return;
        }

        if (Date.now() - startTime > timeoutMs) {
          clearTimeout(timer);
          reject(new Error('Generation timed out. Please try again.'));
          return;
        }

        // Increase interval slightly over time (max 6s) to reduce server load
        interval = Math.min(interval + 500, MAX_INTERVAL);
        timer = setTimeout(check, interval);
      } catch (err) {
        // Network blip — retry without crashing
        timer = setTimeout(check, interval);
      }
    };

    timer = setTimeout(check, 1500); // first check after 1.5s
  });
}

export async function getActiveJobs(token) {
  const r = await axios.get(`${API}/jobs/user/active`, getHeaders(token));
  return r.data.jobs || [];
}

// ── Angles Generation ───────────────────────────────────────────────────────
export async function generateAngles(token, { sourceImage, model, horizontal_angle, vertical_angle, distance, wideAngle, prompt }) {
  const fd = new FormData();
  if (sourceImage) {
    if (typeof sourceImage === 'string') fd.append('sourceImageUrl', sourceImage);
    else fd.append('sourceImage', sourceImage);
  }
  if (model) fd.append('model', model);
  if (horizontal_angle !== undefined) fd.append('horizontal_angle', horizontal_angle);
  if (vertical_angle !== undefined) fd.append('vertical_angle', vertical_angle);
  if (distance !== undefined) fd.append('distance', distance);
  if (wideAngle !== undefined) fd.append('wideAngle', String(wideAngle));
  if (prompt !== undefined && prompt !== null) fd.append('prompt', prompt);

  const r = await axios.post(`${API}/generate/angles`, fd, {
    headers: getHeaders(token).headers,
    timeout: 120000,
  });
  return r.data;
}

// ── Image Editing ─────────────────────────────────────────────────────────
// MultiShot
export async function generateMultiShot(token, { sourceImage, aspectRatio, resolution, style }) {
  const fd = new FormData();
  fd.append('sourceImage', sourceImage);
  fd.append('aspectRatio', aspectRatio);
  fd.append('resolution', resolution);
  fd.append('style', style);

  const r = await axios.post(`${API}/multishot/generate`, fd, {
    headers: getHeaders(token).headers,
    timeout: 240000,
  });
  return r.data;
}

export function openMultiShotEvents(token, jobId, onMessage, onError) {
  const url = `${API}/multishot/${encodeURIComponent(jobId)}/events?token=${encodeURIComponent(token)}`;
  const events = new EventSource(url);
  events.onmessage = (event) => {
    try {
      onMessage(JSON.parse(event.data));
    } catch {
      onMessage({ raw: event.data });
    }
  };
  events.onerror = (error) => {
    if (onError) onError(error);
  };
  return events;
}

export async function fetchMultiShotJob(token, jobId) {
  const r = await axios.get(`${API}/multishot/${encodeURIComponent(jobId)}`, getHeaders(token));
  return r.data;
}

export async function fetchLatestMultiShotJob(token) {
  const r = await axios.get(`${API}/multishot/active/latest`, getHeaders(token));
  return r.data.job || null;
}

export async function downloadMultiShotZip(token, jobId) {
  const res = await fetch(`${API}/multishot/${encodeURIComponent(jobId)}/zip`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('ZIP download failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `aifield_multishot_${jobId}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export async function generateEdit(token, { editType, prompt, sourceImage, model, upscaleResolution, enhancement, aspectRatio, imageResolution, quality }) {
  const fd = new FormData();
  fd.append('editType', editType);
  fd.append('prompt', prompt || '');
  if (model) fd.append('model', model);
  if (sourceImage) {
    if (typeof sourceImage === 'string') fd.append('sourceImageUrl', sourceImage);
    else fd.append('sourceImage', sourceImage);
  }
  if (upscaleResolution) fd.append('upscaleResolution', upscaleResolution);
  if (enhancement !== undefined) fd.append('enhancement', String(enhancement));
  if (aspectRatio) fd.append('aspectRatio', aspectRatio);
  if (imageResolution) fd.append('imageResolution', imageResolution);
  if (quality) fd.append('quality', quality);

  const r = await axios.post(`${API}/generate/edit`, fd, {
    headers: getHeaders(token).headers,
    timeout: 120000,
  });
  return r.data;
}

// ── Video Generation ──────────────────────────────────────────────────────
export async function generateVideo(token, {
  prompt, model, mode, resolution, duration, numVideos, audioOn,
  shots, multiShot, startFrame, endFrame, sourceImage,
  aspectRatio, videoMode, elementImages, promptExpansion, referenceUrls, nsfwChecker
}) {
  const fd = new FormData();
  fd.append('prompt', prompt || '');
  fd.append('model', model);
  fd.append('mode', mode || 'text-to-video');
  fd.append('resolution', resolution || '720p');
  fd.append('duration', duration || '5s');
  fd.append('numVideos', String(numVideos || 1));
  fd.append('audioOn', String(!!audioOn));
  if (nsfwChecker !== undefined) fd.append('nsfwChecker', String(nsfwChecker));
  if (aspectRatio) fd.append('aspectRatio', aspectRatio);
  if (videoMode) fd.append('videoMode', videoMode);
  if (promptExpansion !== undefined) fd.append('promptExpansion', String(promptExpansion));
  if (multiShot) fd.append('multiShot', 'true');
  if (shots) fd.append('shots', JSON.stringify(shots));
  if (startFrame) {
    if (typeof startFrame === 'string') fd.append('startFrameUrl', startFrame);
    else if (startFrame.fileUrl) fd.append('startFrameUrl', startFrame.fileUrl);
    else fd.append('startFrame', startFrame);
  }
  if (endFrame) {
    if (typeof endFrame === 'string') fd.append('endFrameUrl', endFrame);
    else if (endFrame.fileUrl) fd.append('endFrameUrl', endFrame.fileUrl);
    else fd.append('endFrame', endFrame);
  }
  if (sourceImage) {
    if (typeof sourceImage === 'string') fd.append('sourceImageUrl', sourceImage);
    else if (sourceImage.fileUrl) fd.append('sourceImageUrl', sourceImage.fileUrl);
    else fd.append('sourceImage', sourceImage);
  }
  // Element reference images for Kling 3.0
  if (elementImages && elementImages.length > 0) {
    const elUrls = [];
    elementImages.forEach(img => {
      if (typeof img === 'string') elUrls.push(img);
      else fd.append('elementImages', img);
    });
    if (elUrls.length > 0) fd.append('elementUrls', JSON.stringify(elUrls));
  }
  // Reference media URLs for Seedance 2
  if (referenceUrls && referenceUrls.length > 0) {
    fd.append('referenceUrls', JSON.stringify(referenceUrls));
  }

  const r = await axios.post(`${API}/generate/video`, fd, {
    headers: getHeaders(token).headers,
    timeout: 900000,
  });
  return r.data;
}

// ── Seedance 2 & Fast — Queue-based submission ────────────────────────────────
// Returns { jobId, creditsRemaining, status: 'queued' } — use pollJob() to wait
export async function generateSeedanceVideo(token, {
  model, prompt, resolution, aspectRatio, duration, generateAudio, nsfwChecker,
  firstFrame, lastFrame, referenceImages, referenceVideos, referenceAudios,
  firstFrameUrl, lastFrameUrl, referenceImageUrls, referenceVideoUrls, referenceAudioUrls,
}) {
  const fd = new FormData();
  if (model) fd.append('model', model);
  fd.append('prompt', prompt || '');
  if (resolution) fd.append('resolution', resolution);
  if (aspectRatio) fd.append('aspectRatio', aspectRatio);
  if (duration !== undefined) fd.append('duration', String(duration));
  fd.append('generateAudio', String(generateAudio !== false));
  if (nsfwChecker !== undefined) fd.append('nsfwChecker', String(!!nsfwChecker));

  // Frame files
  if (firstFrame && typeof firstFrame !== 'string') fd.append('firstFrame', firstFrame);
  else if (firstFrame) fd.append('firstFrameUrl', firstFrame);
  if (firstFrameUrl) fd.append('firstFrameUrl', firstFrameUrl);

  if (lastFrame && typeof lastFrame !== 'string') fd.append('lastFrame', lastFrame);
  else if (lastFrame) fd.append('lastFrameUrl', lastFrame);
  if (lastFrameUrl) fd.append('lastFrameUrl', lastFrameUrl);

  // Uploaded reference files
  if (referenceImages?.length > 0) referenceImages.forEach(f => fd.append('referenceImages', f));
  if (referenceVideos?.length > 0) referenceVideos.forEach(f => fd.append('referenceVideos', f));
  if (referenceAudios?.length > 0) referenceAudios.forEach(f => fd.append('referenceAudios', f));

  // Pre-existing reference URLs
  if (referenceImageUrls?.length > 0) fd.append('referenceImageUrls', JSON.stringify(referenceImageUrls));
  if (referenceVideoUrls?.length > 0) fd.append('referenceVideoUrls', JSON.stringify(referenceVideoUrls));
  if (referenceAudioUrls?.length > 0) fd.append('referenceAudioUrls', JSON.stringify(referenceAudioUrls));

  // Determine timeout: if actual files are being uploaded (not just URLs), give more time
  const hasFileUploads = (referenceImages?.length > 0) || (referenceVideos?.length > 0) ||
    (referenceAudios?.length > 0) || (firstFrame && typeof firstFrame !== 'string') ||
    (lastFrame && typeof lastFrame !== 'string');
  const r = await axios.post(`${API}/generate/seedance`, fd, {
    headers: getHeaders(token).headers,
    timeout: hasFileUploads ? 120000 : 45000, // 2 min for file uploads, 45s for URL-only
  });
  return r.data;
}

// ── Edit Video Generation ─────────────────────────────────────────────────
export async function editVideo(token, {
  model, sourceVideo, prompt, negativePrompt, aspectRatio, resolution, duration,
  referenceImages, promptExpansion, audioSetting
}) {
  const fd = new FormData();
  fd.append('model', model);
  fd.append('prompt', prompt || '');
  if (negativePrompt) fd.append('negativePrompt', negativePrompt);
  if (aspectRatio) fd.append('aspectRatio', aspectRatio);
  if (resolution) fd.append('resolution', resolution);
  if (duration !== undefined) fd.append('duration', String(duration));
  if (promptExpansion !== undefined) fd.append('promptExpansion', String(promptExpansion));
  if (audioSetting) fd.append('audioSetting', audioSetting);
  if (sourceVideo) {
    if (typeof sourceVideo === 'string') fd.append('sourceVideoUrl', sourceVideo);
    else fd.append('sourceVideo', sourceVideo);
  }
  if (referenceImages && referenceImages.length > 0) {
    const urls = [];
    referenceImages.forEach(img => {
      if (typeof img === 'string') urls.push(img);
      else fd.append('referenceImages', img);
    });
    if (urls.length > 0) fd.append('referenceUrls', JSON.stringify(urls));
  }

  const r = await axios.post(`${API}/generate/edit-video`, fd, {
    headers: getHeaders(token).headers,
    timeout: 900000,
  });
  return r.data;
}

// ── Lip Sync Generation ─────────────────────────────────────────────────────
export async function generateLipSync(token, {
  model, sourceVideo, audioFile, mode, prompt,
  sourceTrimStart, sourceTrimEnd, audioTrimStart, audioTrimEnd
}) {
  const fd = new FormData();
  fd.append('model', model);
  fd.append('mode', mode || 'standard');
  if (prompt) fd.append('prompt', prompt);
  if (sourceTrimStart !== undefined) fd.append('sourceTrimStart', String(sourceTrimStart));
  if (sourceTrimEnd !== undefined) fd.append('sourceTrimEnd', String(sourceTrimEnd));
  if (audioTrimStart !== undefined) fd.append('audioTrimStart', String(audioTrimStart));
  if (audioTrimEnd !== undefined) fd.append('audioTrimEnd', String(audioTrimEnd));
  if (sourceVideo) {
    if (typeof sourceVideo === 'string') fd.append('sourceVideoUrl', sourceVideo);
    else fd.append('sourceVideo', sourceVideo);
  }
  if (audioFile) {
    if (typeof audioFile === 'string') fd.append('audioUrl', audioFile);
    else fd.append('audioFile', audioFile);
  }

  const r = await axios.post(`${API}/generate/lip-sync`, fd, {
    headers: getHeaders(token).headers,
    timeout: 600000,
  });
  return r.data;
}

// ── Motion Sync ───────────────────────────────────────────────────────
export async function generateMotionSync(token, {
  model, subjectImage, motionVideo, prompt, mode, characterOrientation, keepSound,
  trimStart, trimEnd
}) {
  const fd = new FormData();
  fd.append('model', model);
  fd.append('prompt', prompt || '');
  fd.append('mode', mode || '720p');
  fd.append('characterOrientation', characterOrientation || 'video');
  fd.append('keepSound', String(keepSound !== false));
  if (trimStart !== undefined) fd.append('trimStart', String(trimStart));
  if (trimEnd !== undefined) fd.append('trimEnd', String(trimEnd));
  if (subjectImage) {
    if (typeof subjectImage === 'string') fd.append('subjectImageUrl', subjectImage);
    else fd.append('subjectImage', subjectImage);
  }
  if (motionVideo) {
    if (typeof motionVideo === 'string') fd.append('motionVideoUrl', motionVideo);
    else fd.append('motionVideo', motionVideo);
  }

  const r = await axios.post(`${API}/generate/motion-sync`, fd, {
    headers: getHeaders(token).headers,
    timeout: 300000,
  });
  return r.data;
}

// ── Subscription ──────────────────────────────────────────────────────────
// (Legacy subscribe removed — use Paddle or bKash APIs)

// ── bKash Manual Flow ─────────────────────────────────────────────────────
export async function submitBkashPayment(token, data) {
  const r = await axios.post(`${API}/pay/bkash`, data, getHeaders(token));
  return r.data;
}

export async function checkBkashStatus(token, trxId) {
  const r = await axios.get(`${API}/pay/status/${trxId}`, getHeaders(token));
  return r.data;
}

export async function waitBkashStatus(token, trxId) {
  const r = await axios.get(`${API}/pay/wait-status/${trxId}`, getHeaders(token));
  return r.data;
}

export async function reportBkashIssue(token, trxId) {
  const r = await axios.post(`${API}/pay/report-issue`, { trxId }, getHeaders(token));
  return r.data;
}

// ── Credit Purchase ───────────────────────────────────────────────────────
// (Legacy purchaseCredits removed — use bKash APIs)

// ── Paddle Payment ────────────────────────────────────────────────────────
export async function createPaddleMemo(token, { plan, billing }) {
  const r = await axios.post(`${API}/payments/paddle/create-memo`, { plan, billing }, getHeaders(token));
  return r.data;
}

export async function verifyPaddlePayment(token, { memoId, transactionId }) {
  const r = await axios.post(`${API}/payments/paddle/verify`, { memoId, transactionId }, getHeaders(token));
  return r.data;
}

// ── Payment History ───────────────────────────────────────────────────────
export async function createLemonSqueezyCheckout(token, payload) {
  const r = await axios.post(`${API}/payments/lemonsqueezy/create-checkout`, payload, getHeaders(token));
  return r.data;
}

export async function checkLemonSqueezyPayment(token, memoId) {
  const r = await axios.get(`${API}/payments/lemonsqueezy/status/${memoId}`, getHeaders(token));
  return r.data;
}

export async function cancelLemonSqueezySubscription(token) {
  const r = await axios.post(`${API}/payments/lemonsqueezy/cancel-subscription`, {}, getHeaders(token));
  return r.data;
}

export async function fetchPaymentHistory(token) {
  const r = await axios.get(`${API}/user/payment-history`, getHeaders(token));
  return r.data.payments || [];
}

// ── Image Upscale ─────────────────────────────────────────────────────────
export async function upscaleImage(token, { model, sourceImage, sourceUrl, upscaleFactor }) {
  const fd = new FormData();
  fd.append('model', model);
  if (upscaleFactor) fd.append('upscaleFactor', upscaleFactor);
  if (sourceImage) fd.append('sourceImage', sourceImage);
  if (sourceUrl) fd.append('sourceUrl', sourceUrl);

  const r = await axios.post(`${API}/generate/upscale`, fd, {
    headers: getHeaders(token).headers,
    timeout: 300000,
  });
  return r.data;
}

// ── Video Upscale ─────────────────────────────────────────────────────────
export async function upscaleVideo(token, { model, sourceVideo, sourceUrl, upscaleFactor, duration }) {
  const fd = new FormData();
  fd.append('model', model);
  if (upscaleFactor) fd.append('upscaleFactor', upscaleFactor);
  if (sourceVideo) fd.append('sourceVideo', sourceVideo);
  if (sourceUrl) fd.append('sourceUrl', sourceUrl);
  if (duration !== undefined) fd.append('duration', String(duration));

  const r = await axios.post(`${API}/generate/video-upscale`, fd, {
    headers: getHeaders(token).headers,
    timeout: 900000,
  });
  return r.data;
}

// ── Plans Info ─────────────────────────────────────────────────────────────
export async function fetchPlans() {
  const r = await axios.get(`${API}/plans`);
  return r.data;
}

// ── Utilities ─────────────────────────────────────────────────────────────
export async function optimizePrompt(token, prompt) {
  const r = await axios.post(`${API}/optimize-prompt`, { prompt }, getHeaders(token));
  return r.data.optimizedPrompt;
}

export async function getJobStatus(token, jobId) {
  const r = await axios.get(`${API}/jobs/${jobId}`, getHeaders(token));
  return r.data;
}

// ── Asset Library ──────────────────────────────────────────────────────────
export async function uploadAssets(token, files) {
  const fd = new FormData();
  files.forEach(f => fd.append('files', f));
  const r = await axios.post(`${API}/assets/upload`, fd, {
    headers: getHeaders(token).headers,
    timeout: 120000,
  });
  return r.data;
}

export async function fetchAssets(token) {
  const r = await axios.get(`${API}/assets`, getHeaders(token));
  return r.data.assets || [];
}

export async function renameAsset(token, id, fileName) {
  const r = await axios.put(`${API}/assets/${id}/rename`, { fileName }, getHeaders(token));
  return r.data;
}

export async function deleteAssets(token, ids) {
  const r = await axios.delete(`${API}/assets`, {
    data: { ids },
    headers: getHeaders(token).headers,
  });
  return r.data;
}

// ── Templates (Explore Creations) ─────────────────────────────────────────
export async function fetchTemplates() {
  const now = Date.now();
  if (templatesCache && now - templatesCacheAt < TEMPLATE_CACHE_TTL) {
    return templatesCache;
  }

  if (templatesRequest) {
    return templatesRequest;
  }

  templatesRequest = axios.get(`${API}/templates`)
    .then(r => {
      templatesCache = r.data.templates || [];
      templatesCacheAt = Date.now();
      return templatesCache;
    })
    .finally(() => {
      templatesRequest = null;
    });

  return templatesRequest;
}

export function getCachedTemplates() {
  return templatesCache || [];
}

export async function refreshTemplates() {
  templatesCache = null;
  templatesCacheAt = 0;
  templatesRequest = null;
  const r = await axios.get(`${API}/templates`);
  templatesCache = r.data.templates || [];
  templatesCacheAt = Date.now();
  return templatesCache;
}

// ── Safety Status ──────────────────────────────────────────────────────────
export async function fetchSafetyStatus(token) {
  const r = await axios.get(`${API}/user/safety-status`, getHeaders(token));
  return r.data;
}

export async function submitAppeal(token, message) {
  const r = await axios.post(`${API}/user/appeal`, { message }, getHeaders(token));
  return r.data;
}
